// src/components/services/supportApi.ts
export type SupportAttachment = {
    id: number;
    file_name: string | null;
    file_path: string | null;
    file_url: string | null;
    mime_type: string | null;
    file_size: number | null;
    telegram_file_id: string | null;
    telegram_media_type: string | null;
    view_url: string;
    download_url: string;
    created_at: string | null;
};

export type SupportMessage = {
    id: number;
    conversation_id: number;
    sender_type: 'user' | 'admin' | 'system';
    sender_telegram_id: string | null;
    sender_name: string | null;
    text: string | null;
    message_type: 'text' | 'image' | 'document' | string;
    reply_to_message_id: number | null;
    is_read: boolean;
    read_at: string | null;
    created_at: string | null;
    attachments: SupportAttachment[];
};

export type SupportConversation = {
    id: number;
    user_id: string;
    status: string;
    telegram_chat_id: number;
    telegram_thread_id: number;
    last_message_at: string | null;
    last_sender_type: string | null;
    unread_count: number;
    created_at: string | null;
    updated_at: string | null;
};

export type SupportMessagesResponse = {
    status: string;
    conversation: SupportConversation | null;
    messages: SupportMessage[];
};

export type SupportUnreadResponse = {
    status: string;
    unread_count: number;
};

const SUPPORT_API_BASE = 'https://supp.shadownet.nl';
const DEFAULT_TIMEOUT_MS = 12000;

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

function shouldRetry(status?: number): boolean {
    return status === 429 || status === 502 || status === 503 || status === 504;
}

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit = {}, timeoutMs = DEFAULT_TIMEOUT_MS): Promise<Response> {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), timeoutMs);

    try {
        return await fetch(input, {
            ...init,
            signal: controller.signal,
        });
    } finally {
        clearTimeout(timeoutId);
    }
}

async function requestJson<T>(url: string, init: RequestInit = {}, options: { timeoutMs?: number; retries?: number } = {}): Promise<T> {
    const retries = options.retries ?? ((init.method || 'GET').toUpperCase() === 'GET' ? 1 : 0);
    let lastError: Error | null = null;

    for (let attempt = 0; attempt <= retries; attempt++) {
        try {
            const response = await fetchWithTimeout(url, init, options.timeoutMs ?? DEFAULT_TIMEOUT_MS);

            if (!response.ok) {
                const errorText = await response.text();
                const error = new Error(errorText || `Request failed with status ${response.status}`);

                if (attempt < retries && shouldRetry(response.status)) {
                    await sleep(400 * (attempt + 1));
                    continue;
                }

                throw error;
            }

            return response.json();
        } catch (error) {
            const isAbortError = error instanceof DOMException && error.name === 'AbortError';
            const isNetworkError = error instanceof TypeError;

            if ((isAbortError || isNetworkError) && attempt < retries) {
                await sleep(400 * (attempt + 1));
                continue;
            }

            lastError = error instanceof Error ? error : new Error('Unknown request error');
        }
    }

    throw lastError || new Error('Request failed');
}

export async function getSupportMessages(userId: string): Promise<SupportMessagesResponse> {
    return requestJson<SupportMessagesResponse>(`${SUPPORT_API_BASE}/support/messages/${userId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function sendSupportTextMessage(userId: string, text: string) {
    return requestJson(`${SUPPORT_API_BASE}/support/message`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            user_id: userId,
            text,
        }),
    }, { retries: 0 });
}

export async function sendSupportMessageWithFiles(
    userId: string,
    text: string,
    files: File[]
) {
    const formData = new FormData();
    formData.append('user_id', userId);

    if (text.trim()) {
        formData.append('text', text.trim());
    }

    files.forEach((file) => {
        formData.append('files', file);
    });

    return requestJson(`${SUPPORT_API_BASE}/support/message-with-files`, {
        method: 'POST',
        body: formData,
    }, { timeoutMs: 20000, retries: 0 });
}

export function buildAttachmentUrl(path: string) {
    return `${SUPPORT_API_BASE}${path}`;
}

export function buildAttachmentDownloadUrl(attachmentId: number) {
    return `${SUPPORT_API_BASE}/support/attachments/${attachmentId}/download`;
}

export async function getSupportUnreadCount(userId: string): Promise<SupportUnreadResponse> {
    return requestJson<SupportUnreadResponse>(`${SUPPORT_API_BASE}/support/unread/${userId}`, {
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
        },
    });
}

export async function markSupportAsRead(userId: string) {
    return requestJson(`${SUPPORT_API_BASE}/support/read/${userId}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
    }, { retries: 0 });
}
